<!-- Right-side sheet pane for referral detail -->
<section class="p-4 pb-0 box-border flex h-full flex-col justify-start bg-shui-base-3 rounded-l-lg">
    <!-- Title row -->
    <div class="flex justify-between items-center text-shui-text-strong">
        <h1 class="text-base font-bold m-0">
            {{ 'request.requestDetail.referral-menu.title' | translate }}
        </h1>
        <button
            (click)="cancel()"
            class="text-shui-text-strong w-8 h-8 bg-transparent border-none hover:cursor-pointer"
        >
            <sh-icon icon="cancel" size="s"></sh-icon>
        </button>
    </div>

    <section class="box-border flex h-full overflow-hidden overflow-y-auto flex-col justify-start pb-6">
        <!-- Header -->
        <section>
            <div class="flex flex-col w-full gap-2">
                <div class="flex justify-between box-border pt-4 h-[60px]">
                    <div class="flex gap-4 items-center">
                        <h2 class="text-sm text-[#457070] dark:text-[#87D2D2] font-thin max-w-[100px] truncate m-0 p-0">
                            {{
                                'request.requestDetail.referral-menu.referral-label'
                                    | translate: { id: data.referral.id }
                            }}
                        </h2>
                        <sh-tag
                            condensed
                            [selectable]="false"
                            [color]="isInternalReferredOut() ? '17, 217, 0' : '221, 111, 255'"
                            [label]="
                                isInternalReferredOut()
                                    ? ('request.requestDetail.referOutDialog.internal' | translate)
                                    : ('request.requestDetail.referOutDialog.external' | translate)
                            "
                        ></sh-tag>
                    </div>

                    @if (isTaskOwner() && !isClosed()) {
                        <sh-button
                            id="referral-menu-target"
                            shape="circle"
                            color="tertiary"
                            icon="more"
                            label="More actions"
                            size="m"
                        ></sh-button>
                        <sh-popover target="referral-menu-target" position="bottom-left" position-fixed-adjustment>
                            <sh-menu-item
                                icon="success-solid"
                                icon-size="xs"
                                [toggle]="false"
                                [label]="'request.requestDetail.referral-menu.close-referral' | translate"
                                (click)="closeReferral()"
                            ></sh-menu-item>
                        </sh-popover>
                    }
                </div>
                <h3 class="text-[21px]/[25px] text-shui-text-strong font-bold p-0 m-0">
                    {{ data.referral.name }}
                </h3>
            </div>
        </section>

        <sh-divider spacing="m"></sh-divider>

        <!-- Content -->
        <div class="flex flex-col gap-4">
            <!-- Assign To collapsible section -->
            <sh-accordion
                [label]="'request.requestDetail.referral-menu.assign-to' | translate"
                expanded
                flat
                class="w-full [--sh-accordion-header-wrapper-min-height:2.75rem] bg-shui-base-2 rounded-sm px-4 py-2"
            >
                <div slot="header" class="flex items-center justify-between w-full">
                    <span class="text-base text-shui-text-strong font-bold">
                        {{ 'request.requestDetail.referral-menu.assign-to' | translate }}
                    </span>
                    @if (supportsReassignment() && isTaskOwner()) {
                        <sh-button
                            [label]="
                                editing()
                                    ? ('request.requestDetail.referral-menu.cancel-reassign' | translate)
                                    : ('request.requestDetail.referral-menu.reassign-button' | translate)
                            "
                            [icon]="editing() ? 'cancel' : 'edit'"
                            icon-position="left"
                            size="max-content"
                            color="secondary"
                            (click)="$event.stopPropagation(); toggleEditing()"
                        ></sh-button>
                    }
                </div>
                <div class="p-4">
                    @if (editing()) {
                        <div class="flex flex-col gap-4">
                            @if (isInternalReferredOut()) {
                                <sh-assignee-picker
                                    [formControl]="assigneeControl"
                                    [label]="'request.requestDetail.referral-menu.assign-to' | translate"
                                    [roles]="data.roles || []"
                                    [businessService]="data.businessService"
                                ></sh-assignee-picker>
                            } @else {
                                <sh-input-text
                                    [formControl]="emailControl"
                                    [label]="'request.requestDetail.referral-menu.assign-to' | translate"
                                    type="email"
                                ></sh-input-text>
                            }
                            <sh-button
                                [label]="'request.requestDetail.referral-menu.confirm-reassign' | translate"
                                icon="checkmark"
                                icon-position="left"
                                color="primary"
                                size="full-width"
                                [disabled]="!hasEffectiveChange()"
                                (click)="submitReassignment()"
                            ></sh-button>
                        </div>
                    } @else {
                        <sh-referred-out
                            [referredOutEmail]="data.referral.referredOutEmail"
                            [referredOutTeam]="data.referral.referredOutTeam"
                            [isInternalReferral]="!!data.referral.isInternalReferredOut"
                        ></sh-referred-out>
                    }
                </div>
            </sh-accordion>

            <!-- Status bar -->
            <dl class="box-border w-full p-4 bg-shui-base-2 flex gap-2 items-center justify-start rounded-sm m-0">
                <div class="flex flex-col items-start gap-[2.5px] bg-transparent box-border p-2 m-0">
                    <dt class="text-xs text-shui-text-subtle m-0">
                        {{ 'request.requestDetail.referral-menu.status-label' | translate }}
                    </dt>
                    <dd class="m-0">
                        <sh-badge size="medium" [label]="statusLabel()" variant="squared"></sh-badge>
                    </dd>
                </div>
                <div class="flex flex-col items-start gap-[2.5px] bg-transparent box-border p-2 m-0">
                    <dt class="text-xs text-shui-text-subtle m-0">
                        {{ 'request.requestDetail.referral-menu.parent-request-label' | translate }}
                    </dt>
                    <dd class="text-sm text-shui-text-strong m-0">MDLA-{{ data.parentDisplayId }}</dd>
                </div>
                <div class="flex flex-col items-start gap-[2.5px] bg-transparent box-border p-2 m-0">
                    <dt class="text-xs text-shui-text-subtle m-0">
                        {{ 'request.requestDetail.referral-menu.latest-assigned-by-label' | translate }}
                    </dt>
                    <dd class="text-sm text-shui-text-strong m-0">{{ data.referral.updateBy || '-' }}</dd>
                </div>
                <div class="flex flex-col items-start gap-[2.5px] bg-transparent box-border p-2 m-0">
                    <dt class="text-xs text-shui-text-subtle m-0">
                        {{ 'request.requestDetail.referral-menu.latest-update-label' | translate }}
                    </dt>
                    <dd class="text-sm text-shui-text-strong m-0">
                        {{ data.referral.updatedAt | date: 'short' : undefined : locale() }}
                    </dd>
                </div>
            </dl>

            <!-- Description collapsible section -->
            <sh-accordion
                [label]="'request.requestDetail.referral-menu.referral-description' | translate"
                expanded
                flat
                class="w-full [--sh-accordion-header-wrapper-min-height:2.75rem] bg-shui-base-2 rounded-sm px-4 py-2"
            >
                <div class="p-4">
                    <p class="text-sm text-shui-text-strong m-0 whitespace-pre-wrap">
                        {{ data.referral.description || '-' }}
                    </p>
                </div>
            </sh-accordion>
        </div>
    </section>
</section>
